import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    CircularProgress,
    ListSubheader,
    Menu,
    MenuItem,
    Typography,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useAuth } from '../contexts/AuthContext';
import { organizationsApi } from '../services/api';
import { getPmHubPathSuffix } from '../utils/orgSwitchPath';
import {
    buildPmOrganizationGroups,
    flattenPmOrganizations,
} from '../utils/organizationGroups';

function OrganizationSwitcher({ variant = 'sidebar' }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { orgId } = useParams();
    const { organization, selectOrganization } = useAuth();
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [switching, setSwitching] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const menuOpen = Boolean(anchorEl);

    const loadOrganizations = useCallback(async () => {
        setLoading(true);
        try {
            const data = await organizationsApi.getAll();
            setOrganizations(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to load organizations:', err);
            setOrganizations([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadOrganizations();
    }, [loadOrganizations]);

    const groups = useMemo(
        () => buildPmOrganizationGroups(organizations),
        [organizations],
    );

    const flatOrganizations = useMemo(() => {
        const all = flattenPmOrganizations(groups);
        if (
            organization?.id
            && !all.some((row) => row.id === organization.id)
        ) {
            return [...all, organization];
        }
        return all;
    }, [groups, organization]);

    const selectedId = useMemo(() => {
        if (orgId != null && orgId !== '') {
            return Number(orgId);
        }
        return organization?.id ?? null;
    }, [orgId, organization?.id]);

    const selectedOrg = useMemo(
        () => flatOrganizations.find((row) => row.id === selectedId) || organization,
        [flatOrganizations, selectedId, organization],
    );

    const handleOpen = (event) => {
        if (!switching) {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSelect = async (nextOrg) => {
        handleClose();

        const nextOrgId = nextOrg?.id;
        if (!nextOrgId || nextOrgId === selectedId) {
            return;
        }

        setSwitching(true);
        try {
            await selectOrganization(nextOrgId);
            const suffix = getPmHubPathSuffix(location.pathname, orgId);
            navigate(`/app/${nextOrgId}${suffix}`);
        } catch (err) {
            console.error('Failed to switch organization:', err);
        } finally {
            setSwitching(false);
        }
    };

    const isToolbar = variant === 'toolbar';

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: isToolbar ? 1 : undefined,
                    py: isToolbar ? 0 : 1,
                }}
            >
                <CircularProgress size={18} />
            </Box>
        );
    }

    if (flatOrganizations.length <= 1) {
        return (
            <Typography
                variant="body2"
                fontWeight={600}
                noWrap
                sx={{ flex: isToolbar ? 1 : undefined, px: isToolbar ? 0 : 1.5 }}
            >
                {selectedOrg?.name?.trim() || organization?.name?.trim() || '—'}
            </Typography>
        );
    }

    return (
        <Box
            sx={{
                flex: isToolbar ? 1 : undefined,
                minWidth: 0,
                ...(isToolbar ? {} : { px: 1.5, pt: 1, pb: 0.5 }),
            }}
        >
            <Button
                variant="outlined"
                onClick={handleOpen}
                disabled={switching}
                fullWidth
                endIcon={<ArrowDropDownIcon />}
                aria-haspopup="listbox"
                aria-expanded={menuOpen}
                aria-label={t('app.layout.organizationSwitcher')}
                sx={{
                    justifyContent: 'space-between',
                    textTransform: 'none',
                    fontWeight: 600,
                    py: isToolbar ? 0.75 : 1,
                    px: 1.5,
                }}
            >
                <Typography component="span" noWrap sx={{ textAlign: 'left', flex: 1 }}>
                    {selectedOrg?.name?.trim() || '—'}
                </Typography>
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                    sx: {
                        maxHeight: 360,
                        minWidth: anchorEl?.offsetWidth || 200,
                    },
                }}
                MenuListProps={{
                    dense: true,
                    sx: { py: 0 },
                }}
            >
                {groups.map(({ parent, managed }) => (
                    <React.Fragment key={parent.id}>
                        <ListSubheader
                            sx={{
                                fontWeight: 700,
                                lineHeight: '32px',
                                backgroundColor: 'background.paper',
                            }}
                        >
                            {parent.name}
                        </ListSubheader>
                        <MenuItem
                            selected={parent.id === selectedId}
                            onClick={() => handleSelect(parent)}
                        >
                            {parent.name}
                        </MenuItem>
                        {managed.map((child) => (
                            <MenuItem
                                key={child.id}
                                selected={child.id === selectedId}
                                onClick={() => handleSelect(child)}
                                sx={{ pl: 3 }}
                            >
                                <Box>
                                    <Typography variant="body2">{child.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {t('hubs.managedOrganization')}
                                    </Typography>
                                </Box>
                            </MenuItem>
                        ))}
                    </React.Fragment>
                ))}
            </Menu>
        </Box>
    );
}

export default OrganizationSwitcher;
